import { useState } from "react";
import { T, TEAM_COLORS, TEAM_NAMES } from "../tokens";

const TABS = ["Player", "Team", "Event", "Note"];

export default function InspectorPanel({
  selectedPlayer, playerStats, frameMap, frames, currentIdx,
  events, notes, setNotes, activeView,
}) {
  const [tab, setTab] = useState("Player");

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",background:T.surface}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
        padding:"10px 12px",borderBottom:`1px solid ${T.border}`}}>
        <div style={{fontSize:11,fontWeight:800,letterSpacing:1,color:T.textDim}}>INSPECTOR</div>
      </div>
      <div style={{display:"flex",borderBottom:`1px solid ${T.border}`}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{
            flex:1,background:"transparent",border:"none",
            borderBottom:`2px solid ${tab===t?T.accent:"transparent"}`,
            color:tab===t?T.accent:T.textSec,padding:"8px 4px",
            cursor:"pointer",fontSize:11,fontWeight:tab===t?700:500,fontFamily:"inherit"}}>
            {t}
          </button>
        ))}
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"12px"}}>
        {tab==="Player" && (
          <PlayerTab selectedPlayer={selectedPlayer} playerStats={playerStats}
            frameMap={frameMap} frames={frames} currentIdx={currentIdx}/>
        )}
        {tab==="Team" && <TeamTab playerStats={playerStats} events={events}/>}
        {tab==="Event" && <EventTab events={events} currentIdx={currentIdx} frames={frames}/>}
        {tab==="Note" && <NoteTab notes={notes} setNotes={setNotes} activeView={activeView}/>}
      </div>
    </div>
  );
}

// ─── Player ──────────────────────────────────────────────────────────────────
function PlayerTab({ selectedPlayer, playerStats, frameMap, frames, currentIdx }) {
  if (!selectedPlayer || !playerStats?.has(selectedPlayer)) {
    return <Empty text="Click a player on the pitch to inspect them."/>;
  }
  const s = playerStats.get(selectedPlayer);
  const color = TEAM_COLORS[s.team_id] || "#fff";

  // Live speed at the current frame, if this player is present in it
  const curFrame = frames[currentIdx];
  const curRow = frameMap?.get(curFrame)?.find(r=>r.tracker_id===selectedPlayer);
  const liveSpeed = curRow ? curRow.speed_kmh.toFixed(1) : null;

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
        <span style={{width:14,height:14,borderRadius:"50%",background:color,flexShrink:0}}/>
        <div style={{fontWeight:800,fontSize:15,color:T.textPri}}>
          {s.role === "goalkeeper" ? "GK" : "Player"} #{s.tracker_id}
        </div>
      </div>

      <Row label="Team" value={TEAM_NAMES[s.team_id] || "—"} />
      <Row label="Live speed" value={liveSpeed ? `${liveSpeed} km/h` : "not on pitch this frame"} highlight={!!liveSpeed}/>
      <Row label="Avg speed" value={`${s.avg_speed} km/h`} bar={s.avg_speed/34} />
      <Row label="Top speed" value={`${s.max_speed} km/h`} bar={s.max_speed/34} />
      <Row label="Distance covered" value={`${s.distance_m.toLocaleString()} m`} />
      <Row label="Frames tracked" value={s.frames} />
      <Row label="Coverage area" value={`${s.coverage_m2.toLocaleString()} m²`} />

      <div style={{marginTop:14,paddingTop:10,borderTop:`1px solid ${T.border}`,
        fontSize:9,color:T.textDim,lineHeight:1.5}}>
        Stamina, reaction time, dominant foot, passing options and pressure level
        aren't produced by the current pipeline — those need a separate
        possession/event model, not just position tracking.
      </div>
    </div>
  );
}

// ─── Team ────────────────────────────────────────────────────────────────────
function TeamTab({ playerStats, events }) {
  if (!playerStats) return <Empty text="No tracking data loaded."/>;
  const teams = [0, 1].map(tid => {
    const rows = [...playerStats.values()].filter(s=>s.team_id===tid);
    const totalDist = rows.reduce((a,s)=>a+s.distance_m,0);
    const avgSpeed = rows.length ? rows.reduce((a,s)=>a+s.avg_speed,0)/rows.length : 0;
    const sprints = events.filter(e=>e.team_id===tid).length;
    return { tid, players: rows.length, totalDist, avgSpeed, sprints };
  });
  return (
    <div>
      {teams.map(t=>(
        <div key={t.tid} style={{marginBottom:16,paddingBottom:14,
          borderBottom:t.tid===0?`1px solid ${T.border}`:"none"}}>
          <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:8}}>
            <span style={{width:10,height:10,borderRadius:2,background:TEAM_COLORS[t.tid]}}/>
            <div style={{fontWeight:700,fontSize:13,color:T.textPri}}>{TEAM_NAMES[t.tid]}</div>
          </div>
          <Row label="Players tracked" value={t.players} />
          <Row label="Total distance" value={`${Math.round(t.totalDist).toLocaleString()} m`} />
          <Row label="Avg team speed" value={`${t.avgSpeed.toFixed(1)} km/h`} />
          <Row label="Sprint events" value={t.sprints} />
        </div>
      ))}
    </div>
  );
}

// ─── Event ───────────────────────────────────────────────────────────────────
function EventTab({ events, currentIdx, frames }) {
  if (!events?.length) return <Empty text="No sprint events detected in this clip."/>;
  const curFrame = frames[currentIdx];
  return (
    <div>
      <div style={{fontSize:9,color:T.textDim,marginBottom:8}}>
        Sprint events (speed ≥ 28 km/h) — pass/shot/tackle detection isn't in the pipeline yet.
      </div>
      {events.map((e,i)=>(
        <div key={i} style={{
          display:"flex",justifyContent:"space-between",alignItems:"center",
          padding:"6px 8px",borderRadius:5,fontSize:11,marginBottom:3,
          background: e.frame===curFrame ? `${T.accent}18` : "transparent",
        }}>
          <span style={{color:TEAM_COLORS[e.team_id],fontWeight:700}}>#{e.tracker_id}</span>
          <span style={{color:T.textSec}}>{e.speed.toFixed(1)} km/h</span>
          <span style={{color:T.textDim,fontSize:10}}>frame {e.frame}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Note ────────────────────────────────────────────────────────────────────
function NoteTab({ notes, setNotes, activeView }) {
  const val = notes[activeView] || "";
  return (
    <div>
      <div style={{fontSize:10,color:T.textDim,marginBottom:8}}>
        Notes for <b style={{color:T.textSec}}>{activeView==="actual" ? "Actual Match" : activeView}</b>
      </div>
      <textarea
        value={val}
        onChange={e=>setNotes(n=>({...n,[activeView]:e.target.value}))}
        placeholder="Jot coaching notes for this view…"
        style={{
          width:"100%",minHeight:180,resize:"vertical",background:T.panel,
          border:`1px solid ${T.border}`,borderRadius:6,color:T.textPri,
          padding:8,fontSize:12,fontFamily:"inherit",boxSizing:"border-box",
        }}
      />
    </div>
  );
}

// ─── Shared bits ─────────────────────────────────────────────────────────────
function Row({ label, value, bar, highlight }) {
  return (
    <div style={{marginBottom:9}}>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:11.5,marginBottom:bar?3:0}}>
        <span style={{color:T.textSec}}>{label}</span>
        <span style={{color:highlight?T.accent:T.textPri,fontWeight:600}}>{value}</span>
      </div>
      {bar!==undefined && (
        <div style={{height:4,borderRadius:2,background:T.border,overflow:"hidden"}}>
          <div style={{height:"100%",width:`${Math.min(100,bar*100)}%`,background:T.accent}}/>
        </div>
      )}
    </div>
  );
}

function Empty({ text }) {
  return <div style={{fontSize:11,color:T.textDim,textAlign:"center",padding:"30px 10px"}}>{text}</div>;
}
